import { dayjs } from "@outschool/time";
import React from "react";

import DateSeparator from "./DateSeparator";
import MessageBubble from "./MessageBubble";
import { useMessageTeacherDrawerContext } from "./MessageTeacherDrawerContext";
import { BubbleMessageData } from "./types";
import {
  isSameGroup,
  useConversationTimeline,
} from "./useConversationTimeline";

interface ConversationTimelineProps {
  messages: BubbleMessageData[];
  conversationKey: string;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

export default function ConversationTimeline({
  messages,
  conversationKey,
  scrollContainerRef,
}: ConversationTimelineProps) {
  const {
    leader,
    teacherName,
    learner,
    debouncedSearch: searchQuery,
    highlightMessageUid,
  } = useMessageTeacherDrawerContext();

  const { matchingUids, firstMatchUid, firstMatchRef } =
    useConversationTimeline({
      messages,
      searchQuery,
      highlightMessageUid,
      conversationKey,
      scrollContainerRef,
    });

  return (
    <>
      {messages.map((message, index) => {
        const prev = messages[index - 1];
        const next = messages[index + 1];
        const showDateSeparator =
          !prev ||
          dayjs(message.sentAt).format("YYYY-MM-DD") !==
            dayjs(prev.sentAt).format("YYYY-MM-DD");
        const isFirstInGroup =
          showDateSeparator || !prev || !isSameGroup(prev, message);
        const isLastInGroup = !next || !isSameGroup(message, next);
        const isMatch = matchingUids.has(message.uid);
        const isFirstMatch = message.uid === firstMatchUid;

        return (
          <React.Fragment key={message.uid}>
            {showDateSeparator && (
              <DateSeparator
                date={dayjs(message.sentAt).format("MMMM D, YYYY")}
              />
            )}
            <MessageBubble
              ref={isFirstMatch ? firstMatchRef : undefined}
              message={message}
              teacher={leader}
              teacherName={teacherName}
              learnerAvatar={learner.avatar}
              showTimestamp={isFirstInGroup}
              showAvatar={isFirstInGroup}
              isLastInGroup={isLastInGroup}
              highlighted={isMatch}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}
